import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorBanner, ListState, formatUtc, useAsync } from '../../components/Async'
import { Explainer } from '../../components/Explainer'
import { Icon } from '../../components/Icon'
import { StatTiles } from '../../components/charts/StatTiles'
import { DEVICES_COPY, emptyStateFor } from '../../lib/emptyState'
import { api, coverage, seatLimit } from '../../lib/api'
import { nextOffset, pageState, previousOffset } from '../explore/paging'
import { deviceState, envelopeSchema, hasCheckedIn } from './deviceHealth'
import { fleetRibbon } from './fleetRibbon'

/** One page of the fleet. The server's own default, stated here so the paging arithmetic can use it. */
const PAGE = 50

const DEVICES = { one: 'device', many: 'devices' }

/**
 * Which machines exist, and which of them this server can actually reach.
 *
 * The list, and nothing else. It used to open on four organization-level cards - seats, tokens,
 * credential lifetime, and a token reveal panel - and the table began six thousand nine hundred pixels
 * down the page. They have moved to the tabs that answer the question each of them answers; this
 * screen is the fleet (D-429).
 *
 * Three things it now says that it could not say before, and each one was a fact already in the
 * browser or one request away:
 *
 * It says how many devices there are. `listDevices` has accepted an offset and returned `total` since
 * Phase 1 and the screen sent neither and read only `.items`, so nine hundred and eighty-seven devices
 * rendered as fifty rows with nothing indicating the other nine hundred and thirty-seven (D-430).
 *
 * It says which machines have never been reached. Every row carried a green `active` badge, including
 * the nine hundred and sixty-seven that had never once polled the feed - `active` is the enrollment
 * status, and it was standing where an operator's eye looks for a verdict (D-431).
 *
 * And it says what the fleet looks like as a whole, from the server's own fleet-wide counts rather
 * than from the fifty rows in front of it. Counting the rows would have been free and would have
 * reported a page as a fleet.
 */
export function DeviceListPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [offset, setOffset] = useState(0)

  const devices = useAsync(
    () => api.listDevices({ search: search || undefined, status: status || undefined, offset, limit: PAGE }),
    [search, status, offset]
  )
  const fleet = useAsync(() => coverage.get(), [])
  const seats = useAsync(() => seatLimit.get(), [])

  const page = devices.value
  const rows = page?.items ?? []
  const paging = page ? pageState(page.total, page.offset, rows.length, DEVICES) : null

  /** Any change to the query starts at the first page, or page four of the old result is page four of the new one. */
  function searchFor(value: string) {
    setSearch(value)
    setOffset(0)
  }

  function statusOf(value: string) {
    setStatus(value)
    setOffset(0)
  }

  return (
    <>
      <p className="lede">
        Every agent enrolled into this organization. <strong>Checked in</strong> is the only thing that
        says a machine has spoken to the policy feed — a device can be reporting telemetry all
        day and still be running nothing.
      </p>

      <ErrorBanner message={devices.error} />

      {/*
        Fleet-wide, from `/api/v1/coverage`, which is the same count the shell's coverage strip shows.
        Deliberately not reduced over `rows`: that version is three lines, needs no request, and reports
        the first fifty devices as though they were the fleet.
      */}
      <StatTiles stats={fleetRibbon({ coverage: fleet.value, seats: seats.value })} />

      <div className="card">
        <div className="card-body toolbar">
          <input
            className="grow"
            type="search"
            placeholder="Search devices"
            aria-label="Search devices"
            value={search}
            onChange={(event) => searchFor(event.target.value)}
          />
          {/*
            The endpoint has taken a status since Phase 1 and nothing sent one, which left two things
            wrong at once. A retired machine stayed in the list looking like part of the fleet, and it
            was counted in the list's own total while the ribbon - which reads the coverage endpoint,
            and that one does filter to active - counted it out. So the screen showed 988 devices above
            a tile reading 987 and explained neither number.

            Every status by default, because hiding rows is how a device becomes impossible to find.
            The filter is what makes a retired one findable, which the console could not do at all.
          */}
          <select
            aria-label="Status"
            style={{ width: 'auto' }}
            value={status}
            onChange={(event) => statusOf(event.target.value)}
            data-testid="device-status-filter"
          >
            <option value="">Every status</option>
            <option value="active">Active</option>
            <option value="retired">Retired</option>
            <option value="revoked">Revoked</option>
          </select>

          <button type="button" onClick={devices.reload}>
            Refresh
          </button>
        </div>

        <div className="table-scroll">
          <table data-testid="device-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>State</th>
                <th>Last check-in</th>
                <th className="secondary">Person</th>
                <th className="secondary">Last event</th>
                <th className="secondary">Agent</th>
                <th className="secondary">Envelope</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((device) => {
                const state = deviceState(device)

                return (
                  <tr key={device.deviceId} data-testid="device-row">
                    <td>
                      <span className="device-name">
                        <Icon name="desktop-tower" className="faint" />
                        <Link to={`/devices/${device.deviceId}`} data-testid={`device-name-link-${device.deviceId}`}>
                          {device.deviceName}
                        </Link>
                      </span>
                      {device.hostname && device.hostname !== device.deviceName && (
                        <div className="muted">{device.hostname}</div>
                      )}
                    </td>

                    {/* The verdict, not the enrollment status. See `deviceState` for what this replaced. */}
                    <td data-testid={`device-state-${device.deviceId}`}>
                      <span className={state.pill}>{state.label}</span>
                    </td>

                    {/* "never" in words, not an em dash: a machine that has never collected a policy is
                        a fact, and a dash is the absence of one (D-263). */}
                    <td data-testid={`device-last-check-in-${device.deviceId}`}>
                      {hasCheckedIn(device) ? (
                        formatUtc(device.lastCheckInUtc)
                      ) : (
                        <span className="muted">never</span>
                      )}
                    </td>

                    <td className="muted secondary">{device.principalUser ?? '—'}</td>
                    <td className="muted secondary">{formatUtc(device.lastSeenUtc)}</td>
                    <td className="mono secondary">{device.clientVersion}</td>
                    <td className="secondary" data-testid={`device-envelope-${device.deviceId}`}>
                      {envelopeSchema(device).version ?? <span className="muted">not reported</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <ListState
          kind={emptyStateFor({
            value: page?.items ?? null,
            error: devices.error,
            loading: devices.loading,
            filtered: search.trim() !== '' || status !== ''
          })}
          copy={DEVICES_COPY}
          columns={7}
          onAction={() => {
            if (devices.error) return devices.reload()
            setStatus('')
            searchFor('')
          }}
        />

        {paging && rows.length > 0 && (
          <div className="card-body toolbar">
            <span className="hint grow" data-testid="device-count">
              {paging.summary}
            </span>
            <button
              type="button"
              onClick={() => setOffset(previousOffset(offset, PAGE))}
              disabled={!paging.canGoBack}
              data-testid="device-previous"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setOffset(nextOffset(page!.total, offset, PAGE))}
              disabled={!paging.canGoForward}
              data-testid="device-next"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <Explainer summary="What these columns mean, and why a busy device can be running nothing" data-testid="fleet-explainer">
        <p className="muted" style={{ marginTop: 0 }}>
          <strong>Last event</strong> is stamped when telemetry arrives. <strong>Last check-in</strong>{' '}
          is stamped when the device polls the policy feed. They are different clocks, and a machine
          that publishes events every minute without ever checking in is enrolled, visible, and running
          nothing that was published to it.
        </p>
        <p className="muted">
          <strong>State</strong> answers whether this server can reach the machine at all, which is not
          the same question as whether it is enrolled. A device that has never checked in has never
          collected a policy, never pinned this organization's signing key, and never reported
          which envelope format it can read — so anything published to it is waiting, whatever its
          enrollment status says.
        </p>
        <p className="muted">
          <strong>Envelope</strong> is the policy format the device reported it can open, and it is
          blank until a first check-in rather than defaulting to a number the device never sent. Version
          2 carries a policy of any size; version 1 is capped by the key, so a dictionary-bearing policy
          is withheld from it and the device page says which ones and why.
        </p>
        <p className="hint">
          Person, Last event, Agent and Envelope are dropped on a narrow screen. Device, State and Last
          check-in are the three that answer whether anything is wrong, so they are the three that stay.
        </p>
      </Explainer>
    </>
  )
}
